import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Editor as TipTapEditor, useEditor } from "@tiptap/react";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachContactsContext } from "contexts/OutreachContacts";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Email, MessageStatus } from "schemas/dashboard";
import { isOnPage, routes } from "schemas/routes";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import Drawer from "components/Drawer";
import Editor from "components/Editor";
import MergeTagWarning from "components/Editor/MergeTagWarning";
import { extensions } from "components/Editor/utils";
import { getPageName } from "constants/trackingProps";
import { fetcherAuth } from "utils/api";
import { generateColorFromString } from "utils/color";
import { navigateBackPath } from "utils/navigation";
import { hideEmail } from "utils/string";
import { formatEpochTimestamp } from "utils/time";
import { makeDeepCopy, removeFromList } from "utils/updateLocalState";

import AttachmentManager from "../../AttachmentManager";
import SelectTemplate from "./SelectTemplate";
import ScheduledEmailSubject from "./Subject";
import styles from "./styles";

export default function ScheduledEmail() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { scheduledId, outreachContactId } = useParams();
  const scheduledEmailId = scheduledId ? Number(scheduledId) : null;
  const { currentOrg, currentUser } = useContext(OrganizationUserContext);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const {
    currentContact,
    currentContactScheduledEmailsLoading,
    currentContactScheduledEmails,
    setCurrentContactScheduledEmails,
    contacts,
    fetchContact,
    setContacts,
    fetchEmailThreads,
    fetchScheduledEmails,
  } = useContext(OutreachContactsContext);
  const { currentDraftBody, setCurrentDraftBody } = useContext(
    OutreachTemplatesContext,
  );
  const [scheduledEmail, setScheduledEmail] = useState<Email | undefined>();
  const [attachmentDialogOpen, setAttachmentDialogOpen] =
    useState<boolean>(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [previousTemplateId, setPreviousTemplateId] = useState<number | null>(
    null,
  );
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const scheduledEmailOutreachId =
    currentContactScheduledEmails[0]?.outreachContactId;

  const handleBack = () => {
    if (scheduledEmail?.isChanged) {
      setConfirmDialogOpen(true);
      return;
    }
    navigate(navigateBackPath(location.pathname, 2));
  };

  const closeDrawer = () => {
    navigate(`/${routes.inbox}`);
  };

  useEffect(() => {
    setScheduledEmail(
      currentContactScheduledEmails.find(
        (email: Email) => email.id === scheduledEmailId,
      ),
    );
  }, [currentContactScheduledEmails, scheduledEmailId]);

  const handleChangeTemplate = async (templateId: number) => {
    if (!currentOrg?.id || !currentContact?.id) {
      return;
    }

    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/outreach-contacts/${currentContact?.id}/preview`,
        "POST",
        {},
        {
          templateId,
        },
      );
      setScheduledEmail((prev) => {
        if (prev && editor) {
          editor.commands.setContent(res.preview.body);
          return {
            ...prev,
            isChanged: true,
            body: res.preview.body,
            subject: res.preview.subject,
            attachment: res.preview.attachment,
            missingValues: res.preview.missingValues || [],
            templateId,
          };
        }
      });
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleSaveChanges = async () => {
    if (!currentOrg?.id || !scheduledEmail?.id) {
      return;
    }

    try {
      setSaveLoading(true);
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/emails/${scheduledEmail?.id}`,
        "PATCH",
        {},
        {
          overrideSubject: scheduledEmail?.subject,
          overrideBody: scheduledEmail?.body,
          overrideAttachment: scheduledEmail?.attachment,
          overrideTemplateId: scheduledEmail?.templateId,
          previousTemplateId,
        },
      );
      setAlert("Successfully saved changes!", "success");
      setPreviousTemplateId(scheduledEmail?.templateId || null);
      if (currentContact?.id) {
        fetchScheduledEmails(currentContact.id);
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCancelMessage = async () => {
    if (!currentOrg?.id || !currentContact?.id) {
      return;
    }

    setCancelLoading(true);
    try {
      const result = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-contacts/${currentContact?.id}/cancel-message`,
        "POST",
        {},
        {},
        true,
        false,
        false,
      );
      if (result.ok) {
        removeFromList(contacts, setContacts, currentContact, "id");
        removeFromList(
          currentContactScheduledEmails,
          setCurrentContactScheduledEmails,
          scheduledEmail,
          "id",
        );
        // navigate back to inbox or tracking tab as all scheduled emails are canceled
        if (isOnPage("tracking")) {
          navigate(`/${routes.tracking}`);
        } else {
          navigate(`/${routes.inbox}`);
        }
        setAlert("Successfully cancelled message", "success");
      } else {
        const contentType = result.headers.get("content-type");
        if (contentType !== "application/json") {
          throw new Error(await result.text());
        }
        const body = await result.json();
        // refetch the current contact, which will also update the contacts
        if (currentContact?.id) {
          fetchContact(currentContact.id);
        }
        throw new Error(body.message);
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleSend = async () => {
    if (!currentOrg?.id || !scheduledEmail?.id) {
      return;
    }

    try {
      setSendLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/emails/${scheduledEmail?.id}/send`,
        "POST",
        {},
        {
          overrideSubject: scheduledEmail?.subject,
          overrideTemplateId: scheduledEmail?.templateId,
          overrideBody: scheduledEmail?.body,
          overrideAttachment: scheduledEmail?.attachment,
          sendFrom: getPageName(),
        },
      );
      if (res.email.status === MessageStatus.COMPLETED) {
        setAlert("Successfully sent email!", "success");
        if (currentContact?.id) {
          fetchEmailThreads(currentContact.id, true);
          fetchScheduledEmails(currentContact.id);
          fetchContact(currentContact.id);
        }
        if (res.email.gmailThreadId && currentContact?.id) {
          navigate(
            `/${routes.inboxMain}/${currentContact.id}/email/${res.email.gmailThreadId}`,
          );
        }
      } else {
        setAlert(
          <>
            Something went wrong, unable to send email. This is most likely
            because you need to reconnect to Google. You can reconnect to Google
            in your{" "}
            <Link style={{ color: "black" }} to={`/${routes.settings}`}>
              settings page
            </Link>
          </>,
          "warning",
        );
      }
    } catch (error) {
      setAlert(
        "Something went wrong, unable to send email. Please refresh page and retry again.",
        "error",
      );
    } finally {
      setSendLoading(false);
    }
  };

  const editor = useEditor(
    {
      extensions: extensions(),
      content: scheduledEmail?.body || "",
      onUpdate({ editor }) {
        setScheduledEmail((prev) => {
          if (prev) {
            return { ...prev, isChanged: true, body: editor.getHTML() };
          }
        });
      },
    },
    [scheduledEmail?.id],
  ) as TipTapEditor;

  useEffect(() => {
    if (
      scheduledEmailId ||
      currentContactScheduledEmailsLoading ||
      currentContact?.id !== Number(outreachContactId)
    ) {
      return;
    }
    if (!scheduledEmailId) {
      if (
        currentContactScheduledEmails.length > 0 &&
        scheduledEmailOutreachId
      ) {
        navigate(`${currentContactScheduledEmails[0].id}`, { replace: true });
      } else {
        navigate(navigateBackPath(location.pathname, 1), { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentContact?.id,
    currentContactScheduledEmails.length,
    scheduledEmailOutreachId,
    outreachContactId,
    currentContactScheduledEmailsLoading,
  ]);

  useEffect(() => {
    if (
      editor &&
      currentDraftBody?.length > 0 &&
      editor.getHTML() !== currentDraftBody
    ) {
      editor.commands.setContent(makeDeepCopy(currentDraftBody), true);
      setCurrentDraftBody("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDraftBody, editor?.getHTML()]);

  useEffect(() => {
    if (scheduledEmail?.templateId) {
      setPreviousTemplateId(scheduledEmail?.templateId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (currentContactScheduledEmailsLoading) {
    return (
      <Grid container justifyContent="center">
        <Skeleton variant="rounded" width="90%" height={50} />
      </Grid>
    );
  }

  if (!scheduledEmailId) {
    return <></>;
  }

  const getDialogContent = () => (
    <>
      {isSmallScreen && (
        <DialogTitle sx={styles.dialogTitleText}>
          <Grid container alignItems="center" gap={2}>
            <IconButton color="secondary" size="small" onClick={handleBack}>
              <Box component="i" className="fa-solid fa-arrow-left" />
            </IconButton>
            {currentContact?.brand?.name}
          </Grid>
        </DialogTitle>
      )}

      <DialogContent sx={styles.dialogContent}>
        <Accordion
          disableGutters
          square
          expanded={true}
          elevation={0}
          sx={{
            width: "100%",
            borderTop: "none",
            borderBottom: "none",
          }}
        >
          {scheduledEmail && (
            <Box sx={styles.subjectContainer}>
              <ScheduledEmailSubject
                scheduledEmail={scheduledEmail}
                setScheduledEmail={setScheduledEmail}
              />
            </Box>
          )}
          <AccordionSummary sx={styles.summary}>
            <Grid container item xs={12}>
              <Grid item xs="auto">
                <Avatar
                  sx={{
                    background: generateColorFromString(
                      currentUser?.email || "",
                    ),
                    textTransform: "uppercase",
                    mr: 1,
                  }}
                >
                  {currentUser?.name?.charAt(0) || currentUser?.email.charAt(0)}
                </Avatar>
              </Grid>

              <Grid item xs sx={styles.summaryText}>
                <Typography>
                  {currentUser?.name || currentUser?.email}{" "}
                  <Typography
                    component="span"
                    color="textSecondary"
                    variant="caption"
                    sx={{ ml: 1 }}
                  >
                    now
                  </Typography>
                </Typography>

                <Typography color="textSecondary" sx={styles.snippet}>
                  to {hideEmail(scheduledEmail?.toEmail || "")}
                </Typography>
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Editor
                editor={editor}
                allowAttachment={true}
                openAttachmentManager={() => {
                  setAttachmentDialogOpen(true);
                }}
                attachment={scheduledEmail?.attachment}
                removeAttachment={() => {
                  setScheduledEmail((prev) => {
                    if (prev) {
                      return { ...prev, isChanged: true, attachment: "" };
                    }
                  });
                }}
                allowImage={true}
                editorDecoratorBelowMenuBar={
                  <>
                    <Box sx={styles.scheduledAtContainer}>
                      Scheduled to send:{" "}
                      {formatEpochTimestamp({
                        epochTimestamp:
                          scheduledEmail?.scheduledAt ||
                          scheduledEmail?.createdAt ||
                          0,
                        fullDate: true,
                      })}
                    </Box>
                    {scheduledEmail && (
                      <SelectTemplate
                        scheduledEmail={scheduledEmail}
                        handleChangeTemplate={handleChangeTemplate}
                      />
                    )}
                  </>
                }
              />

              <Box sx={{ mt: 2, ml: -2 }}>
                {editor && (
                  <MergeTagWarning
                    editor={editor}
                    subject={scheduledEmail?.subject}
                    missingValues={scheduledEmail?.missingValues || []}
                  />
                )}
              </Box>

              <Grid
                container
                justifyContent="flex-end"
                item
                xs={12}
                gap={1}
                sx={{ my: 2 }}
              >
                <LoadingButton
                  loading={cancelLoading}
                  onClick={handleCancelMessage}
                >
                  Delete
                </LoadingButton>
                <LoadingButton onClick={handleSend} loading={sendLoading}>
                  Send Now
                </LoadingButton>
                <LoadingButton
                  disableElevation
                  variant="contained"
                  disabled={!scheduledEmail?.isChanged}
                  loading={saveLoading}
                  onClick={handleSaveChanges}
                >
                  Save changes
                </LoadingButton>
              </Grid>

              <AttachmentManager
                enteredSelection={(attachment: string) => {
                  setScheduledEmail((prev) => {
                    if (prev) {
                      return { ...prev, isChanged: true, attachment };
                    }
                  });
                }}
                open={attachmentDialogOpen}
                setOpen={setAttachmentDialogOpen}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
    </>
  );

  if (!isSmallScreen) {
    return getDialogContent();
  }

  return (
    <Drawer open={true} onClose={closeDrawer}>
      {getDialogContent()}
      <ConfirmDialog
        header={`Are you sure?`}
        subheader={
          "You have unsaved changes. Are you sure you want to navigate away from this page?"
        }
        open={confirmDialogOpen}
        handleClose={() => setConfirmDialogOpen(false)}
        handleConfirm={() => navigate(-1)}
      />
    </Drawer>
  );
}
